import type { AssetTone } from '@/types/domain';

export type VariboxStatusKey = 'available' | 'occupied' | 'damaged' | 'repair' | 'cleaning' | 'blocked';
export type VariboxSealType = 'VITON' | 'EPDM';
export type VariboxContractValue = 'tak' | 'nie';
export type VariboxFilterValue = '' | VariboxStatusKey;

export interface VariboxHistoryEntry {
  date: string;
  location: string;
  description: string;
}

export interface VariboxRecord {
  serialNumber: string;
  radexId: string;
  capacityL: 250 | 1000;
  bodyColor: string;
  sealType: VariboxSealType;
  status: VariboxStatusKey;
  client: string;
  contract: VariboxContractValue;
  rfid: 'assigned' | 'brak';
  lastLocation: string;
  lastMovement: string;
  lastUpdatedAt: string;
  history: VariboxHistoryEntry[];
}

export const variboxStatusConfig: Record<VariboxStatusKey, { tone: AssetTone; labelKey: string }> = {
  available: { tone: 'success', labelKey: 'assets.statuses.available' },
  occupied: { tone: 'info', labelKey: 'assets.statuses.occupied' },
  damaged: { tone: 'error', labelKey: 'assets.statuses.damaged' },
  repair: { tone: 'warning', labelKey: 'assets.statuses.repair' },
  cleaning: { tone: 'neutral', labelKey: 'assets.statuses.cleaning' },
  blocked: { tone: 'error', labelKey: 'assets.statuses.blocked' },
};

export const variboxClientOptions = ['Kowalski Logistics', 'Grot', 'Cedrob', 'Prosziaczek'] as const;
export const variboxCapacityOptions = [250, 1000] as const;
export const variboxSealOptions = ['VITON', 'EPDM'] as const;
export const variboxContractOptions = ['tak', 'nie'] as const;

export const variboxRecords: VariboxRecord[] = [
  {
    serialNumber: 'M1251397',
    radexId: 'VB01',
    capacityL: 1000,
    bodyColor: 'niebieski',
    sealType: 'VITON',
    status: 'available',
    client: 'Kowalski Logistics',
    contract: 'tak',
    rfid: 'assigned',
    lastLocation: 'Warszawa - strefa wydań A2',
    lastMovement: 'Zwrot po myciu i kontroli jakości',
    lastUpdatedAt: 'Dzisiaj, 08:14',
    history: [
      { date: '2026-08-11 08:14', location: 'Warszawa - strefa wydań A2', description: 'Zamknięto cykl mycia i wpisano jako wolny.' },
      { date: '2026-08-10 16:32', location: 'Cedrob - magazyn zwrotny', description: 'Odebrany od klienta i przewieziony do mycia.' },
      { date: '2026-08-08 11:07', location: 'Warszawa - rampa 3', description: 'Wydany do bieżącego obiegu.' },
    ],
  },
  {
    serialNumber: 'M1251402',
    radexId: 'VB02',
    capacityL: 250,
    bodyColor: 'niebieski',
    sealType: 'EPDM',
    status: 'occupied',
    client: 'Grot',
    contract: 'tak',
    rfid: 'assigned',
    lastLocation: 'Grot - produkcja linia 2',
    lastMovement: 'Wydanie do produkcji',
    lastUpdatedAt: 'Dzisiaj, 07:48',
    history: [
      { date: '2026-08-11 07:48', location: 'Grot - produkcja linia 2', description: 'Przekazany do bieżącego użycia.' },
      { date: '2026-08-09 13:20', location: 'Warszawa - strefa kompletacji', description: 'Uzupełnienie do partii eksportowej.' },
      { date: '2026-08-05 09:10', location: 'Warszawa - przyjęcie', description: 'Zarejestrowany na magazynie jako wolny.' },
    ],
  },
  {
    serialNumber: 'M1251408',
    radexId: 'VB03',
    capacityL: 1000,
    bodyColor: 'niebieski',
    sealType: 'VITON',
    status: 'repair',
    client: 'Cedrob',
    contract: 'tak',
    rfid: 'assigned',
    lastLocation: 'Serwis - hala napraw',
    lastMovement: 'Otwarto zlecenie naprawy',
    lastUpdatedAt: 'Wczoraj, 17:10',
    history: [
      { date: '2026-08-10 17:10', location: 'Serwis - hala napraw', description: 'Przekazany do naprawy uszczelnienia.' },
      { date: '2026-08-08 10:24', location: 'Cedrob - zwrot', description: 'Zgłoszono nieszczelność po odbiorze.' },
      { date: '2026-08-02 08:43', location: 'Warszawa - wydanie', description: 'Wydany do klienta na podstawie umowy.' },
    ],
  },
  {
    serialNumber: 'M1251411',
    radexId: 'VB04',
    capacityL: 250,
    bodyColor: 'niebieski',
    sealType: 'EPDM',
    status: 'damaged',
    client: 'Prosziaczek',
    contract: 'nie',
    rfid: 'brak',
    lastLocation: 'Serwis - sekcja oceny szkód',
    lastMovement: 'Oznaczono jako uszkodzony',
    lastUpdatedAt: 'Wczoraj, 15:30',
    history: [
      { date: '2026-08-10 15:30', location: 'Serwis - sekcja oceny szkód', description: 'Zabezpieczony po uszkodzeniu bocznej ściany.' },
      { date: '2026-08-10 09:45', location: 'Prosziaczek - magazyn zwrotny', description: 'Zwrócony po nieudanej dostawie.' },
      { date: '2026-08-07 12:05', location: 'Warszawa - wydanie', description: 'Wydany bez aktywnej umowy w trybie operacyjnym.' },
    ],
  },
  {
    serialNumber: 'M1251418',
    radexId: 'VB05',
    capacityL: 1000,
    bodyColor: 'niebieski',
    sealType: 'VITON',
    status: 'cleaning',
    client: 'Kowalski Logistics',
    contract: 'tak',
    rfid: 'assigned',
    lastLocation: 'Myjnia - linia 1',
    lastMovement: 'Wysłano do mycia',
    lastUpdatedAt: 'Wczoraj, 13:02',
    history: [
      { date: '2026-08-10 13:02', location: 'Myjnia - linia 1', description: 'Rozpoczęto cykl mycia po zwrocie.' },
      { date: '2026-08-09 18:20', location: 'Kowalski Logistics - punkt odbioru', description: 'Zwrócony po krótkim użyciu.' },
      { date: '2026-08-04 11:54', location: 'Warszawa - wydanie', description: 'Wydany do klienta operacyjnego.' },
    ],
  },
  {
    serialNumber: 'M1251423',
    radexId: 'VB06',
    capacityL: 250,
    bodyColor: 'niebieski',
    sealType: 'EPDM',
    status: 'blocked',
    client: 'Grot',
    contract: 'tak',
    rfid: 'assigned',
    lastLocation: 'Blokada logistyczna - Warszawa',
    lastMovement: 'Zablokowano z powodu zaległości',
    lastUpdatedAt: '08 sie, 11:41',
    history: [
      { date: '2026-08-08 11:41', location: 'Blokada logistyczna - Warszawa', description: 'Zablokowany do czasu rozliczenia zwrotu.' },
      { date: '2026-08-06 16:02', location: 'Grot - odbiór', description: 'Niezamknięta pozycja po stronie klienta.' },
      { date: '2026-08-01 08:22', location: 'Warszawa - wydanie', description: 'Początkowe przypisanie do Grot.' },
    ],
  },
  {
    serialNumber: 'M1251430',
    radexId: 'VB07',
    capacityL: 1000,
    bodyColor: 'niebieski',
    sealType: 'VITON',
    status: 'available',
    client: 'Cedrob',
    contract: 'nie',
    rfid: 'brak',
    lastLocation: 'Warszawa - strefa wolnych zasobów',
    lastMovement: 'Oczekuje na przypisanie',
    lastUpdatedAt: '08 sie, 09:12',
    history: [
      { date: '2026-08-08 09:12', location: 'Warszawa - strefa wolnych zasobów', description: 'Oznaczony jako wolny po kontroli. ' },
      { date: '2026-08-03 15:01', location: 'Cedrob - zwrot', description: 'Zwrócony po zakończeniu umowy.' },
      { date: '2026-07-29 10:45', location: 'Warszawa - wydanie', description: 'Przekazany do pilotażu.' },
    ],
  },
  {
    serialNumber: 'M1251438',
    radexId: 'VB08',
    capacityL: 250,
    bodyColor: 'niebieski',
    sealType: 'EPDM',
    status: 'occupied',
    client: 'Prosziaczek',
    contract: 'tak',
    rfid: 'assigned',
    lastLocation: 'Prosziaczek - linia pakowania',
    lastMovement: 'Wydanie do pakowania',
    lastUpdatedAt: 'Dzisiaj, 06:20',
    history: [
      { date: '2026-08-11 06:20', location: 'Prosziaczek - linia pakowania', description: 'Wydany do bieżącej produkcji.' },
      { date: '2026-08-09 11:30', location: 'Warszawa - zwrot', description: 'Zwrócony i oczyszczony po transporcie.' },
      { date: '2026-08-04 14:15', location: 'Warszawa - wydanie', description: 'Początkowo przypisany do klienta Prosziaczek.' },
    ],
  },
];
